import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AUTH_PROVIDERS, AUTH_PROVIDER_LABELS, BRAND, type AuthProvider } from '@tarot/shared';
import { LogoMark } from '../../brand/BrandLogo';
import { KakaoIcon, NaverIcon } from '../../components/icons';
import { useMe } from './useMe';
import { isToss } from '../../lib/platform';
import { loadTossSdk } from '../../lib/toss';
import { tossLogin } from '../../lib/api';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

/** 로그인 대기 화면에서 이만큼 지나도 이동이 안 되면 홈으로 나갈 길을 보여준다 */
const STUCK_HINT_MS = 5000;

/** provider별 마크 — SVG라 shared(프레임워크 비의존)가 아닌 여기서 매핑 */
const PROVIDER_MARK: Record<AuthProvider, (p: { className?: string }) => JSX.Element> = {
  naver: NaverIcon,
  kakao: KakaoIcon,
};

/**
 * 소셜 로그인 (네이버·카카오). 비회원도 전 기능 사용 가능 —
 * 로그인하면 지난 타로 히스토리 저장·조회 + 닉네임 관리가 열림.
 * 개인정보 미수집: provider 식별키만 저장.
 */
export function LoginPage() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { me } = useMe();
  const qc = useQueryClient();
  const hasError = sp.get('error');
  // 제공사로 이동 중인 provider — 대기 화면 표시 + 이중 클릭 차단
  const [going, setGoing] = useState<AuthProvider | null>(null);
  // 이동이 지연될 때 탈출구를 여는 시점 (즉시 띄우면 정상 흐름에서도 "실패했나" 싶어진다)
  const [stuck, setStuck] = useState(false);
  /** 미니앱 토스 로그인 진행/실패 — 웹의 provider 이동과는 흐름이 다르다 */
  const [tossBusy, setTossBusy] = useState(false);
  const [tossFailed, setTossFailed] = useState(false);
  useEffect(() => {
    if (!going) return setStuck(false);
    const t = setTimeout(() => setStuck(true), STUCK_HINT_MS);
    return () => clearTimeout(t);
  }, [going]);

  // 이미 로그인 상태면 홈으로
  useEffect(() => {
    if (me) navigate('/', { replace: true });
  }, [me, navigate]);

  /**
   * 토스 로그인 — SDK가 준 인가 코드를 서버가 검증하고 토큰을 돌려준다.
   * 성공하면 첫 가입은 닉네임 설정으로, 재로그인은 홈으로 (웹 콜백과 같은 규칙).
   */
  const onTossLogin = async () => {
    if (tossBusy) return;
    setTossBusy(true);
    setTossFailed(false);
    try {
      const sdk = await loadTossSdk();
      if (!sdk) throw new Error('토스 SDK를 불러오지 못했어요');
      // appLogin은 deprecated — TossAuth.login이 정식 (패키지 3.1.1 기준)
      const { isNewUser } = await tossLogin(await sdk.TossAuth.login());
      /**
       * 캐시된 "비회원" 상태를 버린다.
       *
       * 웹 로그인은 제공사로 나갔다 돌아오는 **전체 페이지 이동**이라 앱이 새로 뜨면서
       * 저절로 다시 읽는다. 미니앱은 화면만 갈아끼우므로 `useMe`가 60초 동안 들고 있던
       * null이 그대로 남아, 로그인에 성공하고도 아바타에 로그인 메뉴가 계속 보였다
       * (2026-08-26 실기기 실측). 횟수도 회원 기준으로 다시 읽어야 한다.
       */
      qc.removeQueries({ queryKey: ['me'] });
      void qc.invalidateQueries({ queryKey: ['quota'] });
      navigate(isNewUser ? '/welcome' : '/?login=ok', { replace: true });
    } catch (e) {
      console.error('toss login failed:', e);
      setTossFailed(true);
    } finally {
      setTossBusy(false);
    }
  };

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6">
      <div className="flex flex-col items-center gap-2 text-center">
        <LogoMark className="h-20 w-20 text-gold" />
        <h2 className="font-brand text-2xl font-bold text-gold">{BRAND.nameKo}</h2>
        {/*
          이 화면이 앱의 **첫 화면**이 되는 경로가 있다(딥링크·메뉴 진입). 그때 로그인 버튼만
          보이면 무슨 서비스인지 모르는 채 로그인을 요구받는 꼴이 된다 — 2026-09-07 앱인토스
          심사 반려 사유가 이것이었다("서비스 설명 없이 즉시 토스 로그인을 유도").
          그래서 서비스 소개를 먼저 두고, 로그인이 **선택**이라는 것과 그냥 쓰러 가는 길을 함께 둔다.
        */}
        <p className="max-w-72 text-sm leading-relaxed text-moon">{BRAND.metaDescription}</p>
        <p className="mt-3 text-sm text-moon-dim">
          로그인은 <b className="text-moon">선택</b>이에요 — 하면{' '}
          <b className="text-moon">지난 타로</b>를
          <br />
          다시 볼 수 있고 <b className="text-moon">닉네임</b>이 방에 자동으로 채워져요
        </p>
      </div>

      {hasError && (
        <p className="rounded-xl bg-red-500/15 px-4 py-2 text-sm text-red-300">
          로그인에 실패했어요. 다시 시도해 주세요.
        </p>
      )}

      {tossFailed && (
        <p className="rounded-xl bg-red-500/15 px-4 py-2 text-sm text-red-300">
          토스 로그인에 실패했어요. 다시 시도해 주세요.
        </p>
      )}

      {/*
        미니앱에서는 토스 로그인 하나만 쓴다. 토스 앱 안에서는 이미 토스에 로그인해 있어
        탭 한 번이면 끝나고, 웹뷰에서 네이버·카카오 동의 화면으로 나갔다 돌아오는 동선은
        쿠키가 막혀 세션이 유지되지 않는다 (doc/apps-in-toss-setup.md STEP 6).
      */}
      {isToss ? (
        <div className="flex w-72 flex-col gap-2.5">
          <button
            onClick={onTossLogin}
            disabled={tossBusy}
            className="flex items-center justify-center gap-2 rounded-xl bg-[#0064FF] py-3.5 font-semibold text-white transition hover:brightness-95 disabled:opacity-50"
          >
            {tossBusy ? '로그인 중…' : '토스로 로그인'}
          </button>
        </div>
      ) : (
        /* 버튼 목록·문구·브랜드 색은 shared AUTH_PROVIDER_LABELS 단일 출처. 마크만 여기서 매핑 */
        <div className="flex w-72 flex-col gap-2.5">
          {AUTH_PROVIDERS.map((p) => {
            const label = AUTH_PROVIDER_LABELS[p];
            const Mark = PROVIDER_MARK[p];
            return (
              <a
                key={p}
                href={`${API_BASE}/auth/${p}/start`}
                /**
                 * 제공사로 넘어가기까지 몇 초 걸린다. 그동안 아무 반응이 없으면 눌린 건지 알 수 없어
                 * 연타하게 되므로, 누르는 즉시 대기 화면을 띄우고 추가 클릭을 막는다.
                 *
                 * 기본 이동을 막고 직접 옮기는 이유: 그대로 두면 브라우저가 이동을 먼저 시작해
                 * 대기 화면이 한 번도 그려지지 않는다(실측). 두 번의 rAF로 화면이 칠해진 뒤 옮긴다.
                 */
                onClick={(e) => {
                  e.preventDefault();
                  if (going) return;
                  const { href } = e.currentTarget;
                  setGoing(p);
                  requestAnimationFrame(() =>
                    requestAnimationFrame(() => {
                      location.href = href;
                    }),
                  );
                }}
                aria-disabled={going !== null}
                style={{ backgroundColor: label.bg, color: label.fg }}
                className={`flex items-center justify-center gap-2 rounded-xl border border-white/25 py-3.5 font-semibold transition hover:brightness-95 ${
                  going && going !== p ? 'opacity-40' : ''
                }`}
              >
                <Mark className="h-5 w-5" /> {label.button}
              </a>
            );
          })}
        </div>
      )}

      {/* 로그인을 강요하지 않는다는 걸 화면에서 분명히 — 모든 기능은 비회원도 그대로 쓴다 */}
      <Link
        to="/"
        className="text-sm text-moon-dim underline underline-offset-4 transition hover:text-moon"
      >
        로그인 없이 타로 보러 가기
      </Link>

      <p className="max-w-72 text-center text-xs text-moon-dim/70">
        이름·이메일 등 개인정보는 받지 않아요. 로그인 식별키만 저장돼요.
      </p>

      {going && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-night/85 px-6 backdrop-blur-sm">
          <LogoMark className="tw-brand-spin h-14 w-14 text-gold" />
          <p className="text-center text-sm text-moon-dim">
            <b className="text-moon">{AUTH_PROVIDER_LABELS[going].ko}</b> 로그인 화면으로 이동
            중이에요…
          </p>
          {/* 제공사 응답이 끝내 안 오면 여기서 갇힌다. 잠시 뒤 빠져나갈 길을 준다 */}
          {stuck && (
            <div className="flex flex-col items-center gap-2.5">
              <p className="text-center text-xs text-moon-dim/70">
                생각보다 오래 걸리네요. 네트워크가 느리거나 잠시 문제가 있을 수 있어요.
              </p>
              <Link
                to="/"
                onClick={() => setGoing(null)}
                className="rounded-2xl border border-gold/50 bg-gold/10 px-6 py-2.5 text-sm font-semibold text-gold transition hover:bg-gold/20"
              >
                홈으로 돌아가기
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
